import { useEffect, useState } from 'react';
import { fetchDeclarations, validateDeclaration } from '../services/api';
import { useNavigate } from 'react-router-dom';

const ACCOMMODATION_URL = import.meta.env.VITE_ACCOMMODATION_URL
  || (window.location.hostname.includes('dev.') ? 'https://devhotel.dief360.com' : 'https://hebergement.dief360.com');

function statusBadge(status: string) {
  const s = (status || '').toUpperCase();
  const cls = s === 'AUTO_GENERATED' ? 'blue'
    : s === 'VALIDATED' || s === 'DECLARED' ? 'green'
    : s === 'PAID' ? 'green'
    : s === 'UNDER_REVIEW' ? 'orange'
    : s === 'REJECTED' ? 'red'
    : 'gray';
  const label: Record<string, string> = {
    AUTO_GENERATED: 'Générée',
    UNDER_REVIEW: 'En révision',
    VALIDATED: 'Validée',
    DECLARED: 'Déclarée',
    PAID: 'Payée',
    TRANSMITTED: 'Transmise',
    REJECTED: 'Rejetée',
  };
  return <span className={`badge ${cls}`}>{label[s] || status || '–'}</span>;
}

function fmt(n?: number): string {
  if (n == null || isNaN(n)) return '–';
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function DeclarationsPage() {
  const [rows, setRows] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);
  const [validating, setValidating] = useState(false);
  const navigate = useNavigate();

  const handleValidate = async (id: string) => {
    setValidating(true);
    try {
      await validateDeclaration(id);
      const updated = await fetchDeclarations();
      setRows(Array.isArray(updated) ? updated : []);
      const refreshed = (Array.isArray(updated) ? updated : []).find((d: any) => d.id === id);
      if (refreshed) setSelected(refreshed);
    } catch (e: any) {
      alert(e.message || 'Erreur lors de la validation');
    } finally {
      setValidating(false);
    }
  };

  useEffect(() => {
    fetchDeclarations()
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (error) return <div className="error-msg">{error}</div>;
  if (loading) return <div className="loading">Chargement...</div>;

  return (
    <div className="card">
      <h3>Déclarations</h3>
      <div className="table-wrap" style={{ marginTop: 16 }}>
        <table>
          <thead>
            <tr><th>Période</th><th>Établissement</th><th>Montant CA</th><th>FPT (4%)</th><th>Statut</th><th>Action</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--muted)' }}>Aucune déclaration</td></tr>}
            {rows.map((r, i) => {
              const sysAmt = r.system_amount || r.amount_usd || 0;
              const fptAmt = sysAmt * (r.fpt_rate || 0.04);
              const hasCdf = Number(r.collected_cdf || 0) > 0;
              const usdPart = Number(r.collected_usd != null ? r.collected_usd : sysAmt);
              return (
                <tr key={r.id || i}>
                  <td>{r.period || '–'}</td>
                  <td>{r.hotel_name || r.establishment_name || '–'}</td>
                  <td>
                    <div>{fmt(usdPart)} USD</div>
                    {hasCdf && (
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                        + {Number(r.collected_cdf).toLocaleString('fr-FR')} CDF
                      </div>
                    )}
                  </td>
                  <td>
                    <div><strong>{fmt(fptAmt)} USD</strong></div>
                    {hasCdf && (
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                        ({fmt(usdPart * (r.fpt_rate || 0.04))} USD + {Math.round(Number(r.collected_cdf) * (r.fpt_rate || 0.04)).toLocaleString('fr-FR')} CDF)
                      </div>
                    )}
                  </td>
                  <td>{statusBadge(r.status)}</td>
                  <td>
                    <button className="btn" onClick={() => setSelected(selected?.id === r.id ? null : r)}>
                      {selected?.id === r.id ? 'Fermer' : 'Ouvrir'}
                    </button>
                    {r.status === 'AUTO_GENERATED' && (
                      <button className="btn primary" style={{ marginLeft: 8 }} disabled={validating} onClick={() => handleValidate(r.id)}>
                        {validating ? '...' : 'Valider'}
                      </button>
                    )}
                    {(r.status === 'VALIDATED' || r.status === 'DECLARED') && (
                      <button className="btn primary" style={{ marginLeft: 8 }} onClick={() => navigate('/payments')}>
                        Payer
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="card" style={{ marginTop: 16, background: 'var(--bg-alt, #f8f9fa)' }}>
          <h4>Détails — {selected.declaration_reference || selected.period}</h4>
          <div className="grid2" style={{ marginTop: 12 }}>
            <div><small>Référence</small><div>{selected.declaration_reference || '–'}</div></div>
            <div><small>Période</small><div>{selected.period}</div></div>
            <div><small>Établissement</small><div>{selected.hotel_name || '–'}</div></div>
            <div><small>Province</small><div>{selected.hotel_province || '–'}</div></div>
            <div><small>Total Encaissé USD</small><div>{fmt(selected.collected_usd ?? selected.system_amount)} USD</div></div>
            <div><small>Total Encaissé CDF</small><div>{selected.collected_cdf != null ? `${Number(selected.collected_cdf).toLocaleString('fr-FR')} CDF` : '0 CDF'}</div></div>
            {selected.exchange_rate && (
              <div><small>Taux de change appliqué</small><div>1 USD = {Number(selected.exchange_rate).toLocaleString('fr-FR')} CDF</div></div>
            )}
            <div><small>Base imposable totale (USD)</small><div>{fmt(selected.system_amount)} USD</div></div>
            <div><small>FPT dû (4%)</small><div><strong>{fmt((selected.system_amount || 0) * (selected.fpt_rate || 0.04))} USD</strong></div></div>
            <div><small>Statut</small><div>{statusBadge(selected.status)}</div></div>
            <div><small>Généré le</small><div>{selected.generated_at ? new Date(selected.generated_at).toLocaleDateString('fr-FR') : '–'}</div></div>
          </div>
          {selected.hotel_id && (
            <a
              href={`${ACCOMMODATION_URL}/guests?hotel_id=${selected.hotel_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn"
              style={{ marginTop: 12, display: 'inline-block' }}
            >
              Voir profil hébergement ↗
            </a>
          )}
        </div>
      )}
    </div>
  );
}
